import React, { useRef } from "react";
import { toast } from "react-hot-toast";
import { FaRedo } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import Button from "../../components/button-v1";
import Spinner from "../../components/spinner";
import DataBoundsFigmaFields from "./data-bounds/DataBoundsFigmaFields";
import styles from "../../styles/patient-page-modules/OrgDataBounds.module.css";
import {
  getOrgDataBounds,
  setOrgDataBounds,
  resetOrgDataBounds,
} from "../../services/tenant/dataBounds";
import { handleApiError } from "../../utils/handleApiError";

/**
 * Organization Data Bounds Modal
 * Allows admin to configure organization-level data bounds for all metrics.
 * UI matches the Figma design; form logic/validation is shared with the
 * patient-settings DataBounds component via useDataBoundsForm.
 * Uses TanStack Query for API calls.
 */
const OrgDataBoundsModal = ({ isOpen, onClose, onSaved }) => {
  const dataBoundsRef = useRef(null);
  const queryClient = useQueryClient();
  const [showResetConfirm, setShowResetConfirm] = React.useState(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);

  const handleFormChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  // Fetch organization data bounds
  const {
    data: orgDataBounds,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["orgDataBounds"],
    queryFn: getOrgDataBounds,
    enabled: isOpen,
    onError: (err) => handleApiError(err),
  });

  // Reset dirty state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsFormDirty(false);
    }
  }, [isOpen]);

  // Reset dirty state when data successfully loads
  React.useEffect(() => {
    if (orgDataBounds && !isLoading) {
      setIsFormDirty(false);
    }
  }, [orgDataBounds, isLoading]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (bounds) => setOrgDataBounds(bounds),
    onSuccess: () => {
      toast.success("Organization data bounds updated successfully");
      queryClient.invalidateQueries({ queryKey: ["orgDataBounds"] });
      onSaved?.();
      onClose();
    },
    onError: (err) => {
      handleApiError(err);
      toast.error("Failed to update data bounds. Please try again.");
    },
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: () => resetOrgDataBounds(),
    onSuccess: () => {
      toast.success("Data bounds reset to default values");
      queryClient.invalidateQueries({ queryKey: ["orgDataBounds"] });
      setShowResetConfirm(false);
      onSaved?.();
    },
    onError: (err) => {
      handleApiError(err);
      toast.error("Failed to reset data bounds. Please try again.");
    },
  });

  // Validate that low < high for all metrics
  const validateBounds = (bounds) => {
    const errors = {};
    const metrics = [
      "glucose",
      "systolic",
      "diastolic",
      "heart_rate",
      "temperature",
      "weight",
      "oxygen",
    ];

    metrics.forEach((metric) => {
      const low = bounds[`${metric}_low`];
      const high = bounds[`${metric}_high`];

      if (
        low !== null &&
        low !== undefined &&
        low !== "" &&
        high !== null &&
        high !== undefined &&
        high !== "" &&
        Number(low) >= Number(high)
      ) {
        errors[metric] = "Low must be less than High";
      }
    });

    return errors;
  };

  const handleSave = async () => {
    if (!dataBoundsRef.current) return;

    // Get validated data from the fields component
    const validatedData = await dataBoundsRef.current.getValidatedData();
    if (!validatedData) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    // Validate low < high constraint
    const errors = validateBounds(validatedData);
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    // Save via mutation
    saveMutation.mutate(validatedData);
  };

  const handleReset = () => {
    resetMutation.mutate();
  };

  // Transform org data bounds for the fields component
  const bounds = orgDataBounds?.bounds || {};
  const transformedBounds = {
    glucose_low: bounds.glucose_low?.toString() || "",
    glucose_high: bounds.glucose_high?.toString() || "",
    systolic_low: bounds.systolic_low?.toString() || "",
    systolic_high: bounds.systolic_high?.toString() || "",
    diastolic_low: bounds.diastolic_low?.toString() || "",
    diastolic_high: bounds.diastolic_high?.toString() || "",
    heart_rate_low: bounds.heart_rate_low?.toString() || "",
    heart_rate_high: bounds.heart_rate_high?.toString() || "",
    temperature_low: bounds.temperature_low?.toString() || "",
    temperature_high: bounds.temperature_high?.toString() || "",
    weight_low: bounds.weight_low?.toString() || "",
    weight_high: bounds.weight_high?.toString() || "",
    oxygen_low: bounds.oxygen_low?.toString() || "",
    oxygen_high: bounds.oxygen_high?.toString() || "",
  };

  // Show loading state
  if (isLoading) {
    return (
      <Dialog open={isOpen} setOpen={onClose}>
        <DialogContent className={styles.dialogContent}>
          <div className={styles.loadingContainer}>
            <Spinner />
            <p className={styles.loadingText}>Loading data bounds...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Dialog open={isOpen} setOpen={onClose}>
        <DialogContent className={styles.dialogContent}>
          <DialogHeader>
            <DialogTitle>Data bounds</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className={styles.errorText}>
              Failed to load data bounds. Please try again.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => refetch()}>Retry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {/* Main Edit Modal */}
      <Dialog open={isOpen} setOpen={onClose}>
        <DialogContent className={styles.dialogContent}>
          <DialogHeader>
            <div className={styles.modalHeader}>
              <div className={styles.headerLeft}>
                <div className={styles.iconCircle}>
                  <img
                    src="/assets/images/svgs/databounds.svg"
                    alt="Data bounds"
                    width={23}
                    height={23}
                  />
                </div>
                <div className={styles.titleBlock}>
                  <DialogTitle className={styles.title}>
                    Data bounds
                  </DialogTitle>
                  <p className={styles.subtitle}>Organization thresholds</p>
                </div>
              </div>
              <Button
                variant="normal"
                size="small"
                className={styles.resetButton}
                onClick={() => setShowResetConfirm(true)}
              >
                <FaRedo size={12} />
                Reset to default
              </Button>
            </div>
          </DialogHeader>
          <DialogBody>
            <DataBoundsFigmaFields
              ref={dataBoundsRef}
              data_bounds={transformedBounds}
              onFormChange={handleFormChange}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={!isFormDirty || saveMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Modal */}
      <Dialog open={showResetConfirm} setOpen={() => setShowResetConfirm(false)}>
        <DialogContent className={styles.resetDialogContent}>
          <DialogHeader>
            <DialogTitle>Reset Data Bounds</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-gray-700">
              Are you sure you want to reset all data bounds to the organization's standard clinical defaults?
            </p>
          </DialogBody>
          <DialogFooter className={styles.resetDialogFooter}>
            <Button
              variant="ghost"
              onClick={() => setShowResetConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReset}
              loading={resetMutation.isPending}
            >
              Yes, Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrgDataBoundsModal;